import React from "react";
import { Share, Bell, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

/**
 * Install + notification guide section (replaces previous "Coming on Google Play" teaser).
 * Why: PWA install via "Add to Home Screen" gives users the best push-notification
 * experience right now. The Play Store TWA build is still pending a v4.3 push fix,
 * so we steer users to the web install which actually works end-to-end.
 *
 * Component name kept (GooglePlayTeaser) so we don't have to touch Landing.jsx routing.
 */
const T = {
  sk: {
    eyebrow: "Inštalácia pre iPhone",
    title: "iPhone? Nainštaluj si Tigik z webu",
    sub: "iOS appka ešte príde do App Store, ale plnohodnotnú appku máš už teraz cez Safari na ploche iPhonu — vrátane správ a notifikácií.",
    iosLabel: "iPhone / Safari",
    androidLabel: "Android / Chrome",
    s1: "Otvor",
    s1_ios: "tigik.saubereko.eu v Safari",
    s1_and: "tigik.saubereko.eu v Chrome",
    s2: "Klikni",
    s2_ios: 'Zdieľať → "Pridať na plochu"',
    s2_and: 'Menu ⋮ → "Pridať na plochu"',
    s3: "Otvor Tigik appku z plochy",
    s4: 'Pri prvom otvorení klikni „Zapnúť notifikácie" v popup-e',
    notifsTitle: "🔔 Aby ti chodili upozornenia",
    notifsBody: 'V appke klikni "Zapnúť notifikácie" a povoľ ich v systémových nastaveniach. Bez toho nedostaneš správy keď je appka zatvorená.',
    badge: "Bezplatné · Bez reklám · Bez sledovania",
  },
  en: {
    eyebrow: "Install for iPhone",
    title: "iPhone? Install Tigik from the web",
    sub: "iOS App Store version is coming soon — for now, get the full-featured app via Safari on your iPhone home screen, including real-time messages and notifications.",
    iosLabel: "iPhone / Safari",
    androidLabel: "Android / Chrome",
    s1: "Open",
    s1_ios: "tigik.saubereko.eu in Safari",
    s1_and: "tigik.saubereko.eu in Chrome",
    s2: "Tap",
    s2_ios: 'Share → "Add to Home Screen"',
    s2_and: 'Menu ⋮ → "Add to Home screen"',
    s3: "Open Tigik from the home screen",
    s4: 'On first launch, tap "Enable notifications" in the popup',
    notifsTitle: "🔔 To receive alerts",
    notifsBody: 'Inside the app, tap "Enable notifications" and allow them in system settings. Otherwise you won\'t get messages when the app is closed.',
    badge: "Free · No ads · No tracking",
  },
  cs: {
    eyebrow: "Instalace pro iPhone",
    title: "iPhone? Nainstaluj si Tigik z webu",
    sub: "iOS verze v App Store ještě dorazí — zatím si plnohodnotnou aplikaci přidáš přes Safari na plochu iPhonu, včetně zpráv a notifikací.",
    iosLabel: "iPhone / Safari",
    androidLabel: "Android / Chrome",
    s1: "Otevři",
    s1_ios: "tigik.saubereko.eu v Safari",
    s1_and: "tigik.saubereko.eu v Chrome",
    s2: "Klikni",
    s2_ios: 'Sdílet → "Přidat na plochu"',
    s2_and: 'Menu ⋮ → "Přidat na plochu"',
    s3: "Otevři Tigik z plochy",
    s4: 'Při prvním otevření klikni "Zapnout notifikace" v popupu',
    notifsTitle: "🔔 Aby ti chodila upozornění",
    notifsBody: 'V apce klikni "Zapnout notifikace" a povol je v systémových nastaveních.',
    badge: "Zdarma · Bez reklam · Bez sledování",
  },
  de: {
    eyebrow: "Installation für iPhone",
    title: "iPhone? Installiere Tigik aus dem Web",
    sub: "iOS-Version im App Store kommt bald — vorerst füge dir die vollwertige App über Safari zu deinem iPhone-Startbildschirm hinzu, inklusive Nachrichten und Benachrichtigungen.",
    iosLabel: "iPhone / Safari",
    androidLabel: "Android / Chrome",
    s1: "Öffne",
    s1_ios: "tigik.saubereko.eu in Safari",
    s1_and: "tigik.saubereko.eu in Chrome",
    s2: "Tippe",
    s2_ios: 'Teilen → "Zum Home-Bildschirm"',
    s2_and: 'Menü ⋮ → "Zum Startbildschirm hinzufügen"',
    s3: "Öffne Tigik vom Startbildschirm",
    s4: 'Tippe beim ersten Start auf "Benachrichtigungen aktivieren"',
    notifsTitle: "🔔 Damit Hinweise ankommen",
    notifsBody: 'In der App auf "Benachrichtigungen aktivieren" tippen und in den Systemeinstellungen erlauben.',
    badge: "Kostenlos · Werbefrei · Kein Tracking",
  },
  pl: {
    eyebrow: "Instalacja na iPhone",
    title: "iPhone? Zainstaluj Tigik z sieci",
    sub: "Wersja iOS w App Store dopiero nadchodzi — na razie pełnoprawną aplikację dodasz przez Safari do ekranu głównego iPhone'a, w tym wiadomości i powiadomienia.",
    iosLabel: "iPhone / Safari",
    androidLabel: "Android / Chrome",
    s1: "Otwórz",
    s1_ios: "tigik.saubereko.eu w Safari",
    s1_and: "tigik.saubereko.eu w Chrome",
    s2: "Kliknij",
    s2_ios: 'Udostępnij → "Do ekranu głównego"',
    s2_and: 'Menu ⋮ → "Dodaj do ekranu głównego"',
    s3: "Otwórz Tigik z ekranu głównego",
    s4: 'Przy pierwszym uruchomieniu kliknij "Włącz powiadomienia"',
    notifsTitle: "🔔 Aby otrzymywać powiadomienia",
    notifsBody: 'W aplikacji kliknij "Włącz powiadomienia" i zezwól w ustawieniach systemowych.',
    badge: "Bezpłatnie · Bez reklam · Bez śledzenia",
  },
  fr: {
    eyebrow: "Installation pour iPhone",
    title: "iPhone ? Installe Tigik depuis le web",
    sub: "L'appli iOS arrivera bientôt sur l'App Store, mais tu as déjà une appli complète via Safari sur ton iPhone — avec messagerie et notifs.",
    iosLabel: "iPhone / Safari",
    androidLabel: "Android / Chrome",
    s1: "Ouvre",
    s1_ios: "tigik.saubereko.eu dans Safari",
    s1_and: "tigik.saubereko.eu dans Chrome",
    s2: "Tape sur",
    s2_ios: "Partager → « Sur l'écran d'accueil »",
    s2_and: "Menu ⋮ → « Ajouter à l'écran d'accueil »",
    s3: "Ouvre l'appli Tigik depuis ton écran d'accueil",
    s4: "À la première ouverture, tape « Activer les notifications » dans le popup",
    notifsTitle: "🔔 Pour recevoir les notifications",
    notifsBody: "Dans l'appli, tape « Activer les notifications » et autorise-les dans les réglages système. Sinon, tu ne recevras pas de messages quand l'appli est fermée.",
    badge: "Gratuit · Sans pub · Sans tracking",
  },
};

function StepRow({ n, label }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-7 h-7 bg-gold-500/20 border border-gold-600/40 text-gold-300 text-xs font-bold font-mono flex items-center justify-center">
        {n}
      </div>
      <div className="text-sm text-zinc-200 pt-0.5 leading-relaxed">{label}</div>
    </div>
  );
}

function PlatformCard({ icon: Icon, label, s1, s2, s3, s4, sCommon }) {
  return (
    <div className="bg-zinc-950/80 border border-zinc-800 hover:border-gold-700/40 transition-colors p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
        <Icon className="w-4 h-4 text-gold-400" />
        <span className="text-xs font-mono uppercase tracking-widest text-gold-300">{label}</span>
      </div>
      <div className="space-y-3">
        <StepRow n="1" label={s1} />
        <StepRow n="2" label={s2} />
        <StepRow n="3" label={sCommon.s3} />
        <StepRow n="4" label={sCommon.s4} />
      </div>
    </div>
  );
}

export default function GooglePlayTeaser() {
  const { lang } = useLang();
  const t = T[lang] || T.sk;

  return (
    <section
      id="install-guide"
      data-testid="install-pwa-section"
      className="relative overflow-hidden border-y border-gold-700/30 bg-gradient-to-br from-gold-500/[0.04] via-zinc-950 to-gold-500/[0.06]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(50% 60% at 50% 0%, rgba(212,175,55,0.10), transparent 70%)",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <div className="text-center mb-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3">
            {t.eyebrow}
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white mb-4 leading-tight">
            {t.title}
          </h2>
          <p className="text-sm sm:text-base text-zinc-300 max-w-2xl mx-auto leading-relaxed">
            {t.sub}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            {t.badge}
          </div>
        </div>

        <div className="grid gap-4 mb-8 max-w-xl mx-auto">
          <PlatformCard
            icon={Share}
            label={t.iosLabel}
            s1={`${t.s1} ${t.s1_ios}`}
            s2={`${t.s2}: ${t.s2_ios}`}
            sCommon={t}
          />
        </div>

        <div
          className="border-l-2 border-gold-500 bg-gold-500/[0.04] p-5 flex items-start gap-3"
          data-testid="install-notifs-tip"
        >
          <Bell className="w-5 h-5 text-gold-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-gold-200 mb-1">{t.notifsTitle}</div>
            <div className="text-xs sm:text-sm text-zinc-300 leading-relaxed">{t.notifsBody}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
